// TRE-1584 — habit icons are lucide keys (not emoji), so the icon can be tinted with the habit's
// palette color and stay visually consistent with the rest of the app's iconography. This file is
// the platform-agnostic source of truth: the canonical key set + labels. Each surface (admin,
// mobile, web) maps these keys to its own lucide package's components.

export const HABIT_ICON_KEYS = [
  "circle-check",
  "droplet",
  "glass-water",
  "dumbbell",
  "footprints",
  "bike",
  "waves",
  "activity",
  "heart",
  "heart-pulse",
  "brain",
  "book-open",
  "pencil",
  "moon",
  "sun",
  "alarm-clock",
  "apple",
  "salad",
  "carrot",
  "pill",
  "utensils",
  "coffee",
  "smile",
  "sparkles",
  "flame",
  "target",
  "leaf",
  "ban",
  "smartphone",
] as const;

export type HabitIconKey = (typeof HABIT_ICON_KEYS)[number];

export const DEFAULT_HABIT_ICON_KEY: HabitIconKey = "circle-check";

export const asHabitIconKey = (value: string | null | undefined): HabitIconKey =>
  (HABIT_ICON_KEYS as readonly string[]).includes(value ?? "") ? (value as HabitIconKey) : DEFAULT_HABIT_ICON_KEY;

/** Polish labels for accessibility / tooltips in the picker. */
export const habitIconLabels: Record<HabitIconKey, string> = {
  "circle-check": "Odhaczenie",
  droplet: "Woda",
  "glass-water": "Szklanka wody",
  dumbbell: "Siłownia",
  footprints: "Kroki / spacer",
  bike: "Rower",
  waves: "Pływanie",
  activity: "Aktywność",
  heart: "Zdrowie",
  "heart-pulse": "Cardio",
  brain: "Umysł",
  "book-open": "Czytanie",
  pencil: "Dziennik",
  moon: "Sen",
  sun: "Poranek",
  "alarm-clock": "Przypomnienie",
  apple: "Owoce",
  salad: "Posiłek",
  carrot: "Warzywa",
  pill: "Suplementy",
  utensils: "Jedzenie",
  coffee: "Kawa",
  smile: "Nastrój",
  sparkles: "Pielęgnacja",
  flame: "Energia",
  target: "Cel",
  leaf: "Wellness",
  ban: "Rzucanie nałogu",
  smartphone: "Czas ekranowy",
};

/**
 * Best-effort migration map for the pre-lucide emoji values, used by the SQL backfill and as a
 * runtime fallback so any lingering emoji still resolves to a sensible icon.
 */
export const HABIT_EMOJI_TO_ICON_KEY: Record<string, HabitIconKey> = {
  "✅": "circle-check",
  "💧": "droplet",
  "🏃": "footprints",
  "🏋️": "dumbbell",
  "🧘": "sparkles",
  "🚶": "footprints",
  "🚴": "bike",
  "🏊": "waves",
  "🥗": "salad",
  "🍎": "apple",
  "🥦": "carrot",
  "💊": "pill",
  "😴": "moon",
  "🌙": "moon",
  "☀️": "sun",
  "⏰": "alarm-clock",
  "📖": "book-open",
  "✍️": "pencil",
  "🧠": "brain",
  "❤️": "heart",
  "🦷": "smile",
  "🧴": "sparkles",
  "🚭": "ban",
  "📵": "smartphone",
};

/** Coerce a stored value (lucide key OR legacy emoji) to a valid lucide key. */
export const resolveHabitIconKey = (value: string | null | undefined): HabitIconKey => {
  if (!value) {
    return DEFAULT_HABIT_ICON_KEY;
  }
  if ((HABIT_ICON_KEYS as readonly string[]).includes(value)) {
    return value as HabitIconKey;
  }
  return HABIT_EMOJI_TO_ICON_KEY[value] ?? DEFAULT_HABIT_ICON_KEY;
};
